import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { debounceTime } from 'rxjs/operators';
import {
  IGetEmpresa,
  IGetEmpresaOrdemServicoPaginatedParams
} from 'src/app/services/EmpresaService/empresa.model';
import { EmpresaService } from 'src/app/services/EmpresaService/empresa.service';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { IGetReportBase64 } from 'src/app/services/ReportService/report.model';
import { ReportService } from 'src/app/services/ReportService/report.service';
import Swal from 'sweetalert2';

import { ITabelaOrdemServico } from './listagem-ordem-servico.model';
import { formatarData, formateMoney } from 'src/app/core/utils/utilities';
import { ServicoEntradaService } from 'src/app/services/ServicoEntradaService/servicoEntrada.service';
import {
  IGetEntradaServicoPaginatedResponse,
  IGetServicosParams
} from 'src/app/services/ServicoEntradaService/servicoEntrada.model';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';

type IOptions = {
  name: string;
  value: string | number;
};
@Component({
  selector: 'app-listagem-ordem-servico',
  templateUrl: './listagem-ordem-servico.component.html',
  styleUrl: './listagem-ordem-servico.component.scss'
})
export class ListagemOrdemServicoComponent implements OnInit, AfterViewInit {
  isLoading: boolean = false;
  numeroPagina: number = 0;
  form = this._inicializarFormulario();
  tableData: ITabelaOrdemServico[] | Array<any> = [];
  pesquisaParams = {
    DT_INIC: '',
    DT_FIM: '',
    PAGE_NUMBER: 1,
    PAGE_SIZE: 8
  };
  maxPages: number = 1;
  empresaId: number = 0;
  expandirCardsMobile: boolean = true;

  servicoOptions: IOptions[] = [
    {
      name: '',
      value: ''
    }
  ];

  tableColumns = [
    { header: 'Data da Entrada', key: 'DT_GATE_ENTR', type: 'text' },
    { header: 'Data da Saída', key: 'DT_GATE_SAID', type: 'text' },
    { header: 'Placa do Cavalo', key: 'TX_PLCA_CAVA', type: 'text' },
    { header: 'Nome do Motorista', key: 'NM_MTRT', type: 'text' },
    { header: 'Tipo de Serviço', key: 'TX_TIPO_SRVC', type: 'text' },
    { header: 'Empresa', key: 'NM_FNTS_EPSA', type: 'text' },
    { header: 'Valor', key: 'VL_SRVC', type: 'text' }
  ];
  selectedItem: any;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private empresaService: EmpresaService,
    private reportService: ReportService,
    private appExportBase64: AppExportBase64,
    public swalAlert: SwalAlertService
  ) {}

  private _inicializarFormulario() {
    const form = this.fb.group({
      DT_INIC: this.fb.control(''),
      DT_FIM: this.fb.control('')
    });
    return form;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params: any) => {
      this.empresaId = Number(params['PK_EPSA']);
    });
    this.getOrdemServico();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.getOrdemServico();
    });
  }

  mudarPagina(pagina: number) {
    this.pesquisaParams.PAGE_NUMBER = pagina;
    this.numeroPagina = pagina;

    const payload = {
      DT_SRVC_ENTR_INIC: this.pesquisaParams.DT_INIC,
      DT_SRVC_ENTR_FIM: this.pesquisaParams.DT_FIM,
      PAGE_NUMBER: this.pesquisaParams.PAGE_NUMBER,
      PAGE_SIZE: this.pesquisaParams.PAGE_SIZE
    };

    this.getOrdemServico();
  }

  formatarCampos(campo: string | number | Date): string {
    return campo === null || campo === '' ? 'Não Informado' : String(campo);
  }

  createPayload(): IGetEmpresaOrdemServicoPaginatedParams {
    return {
      FK_EPSA: this.empresaId,
      DT_SRVC_ENTR_FIM: String(this.form.controls.DT_FIM.value),
      DT_SRVC_ENTR_INIC: String(this.form.controls.DT_INIC.value),
      PAGE_NUMBER: this.pesquisaParams.PAGE_NUMBER,
      PAGE_SIZE: this.pesquisaParams.PAGE_SIZE
    };
  }
  getOrdemServico() {
    this.isLoading = true;
    const payload = this.createPayload();

    this.empresaService.GetEmpresaOrdemServicoPaginated(payload).subscribe({
      next: res => {
        this.tableData = res.ITEMS.map(item => {
          return {
            DT_GATE_ENTR: formatarData(item.DT_GATE_ENTR),
            DT_GATE_SAID: formatarData(item.DT_GATE_SAID),
            TX_PLCA_CAVA: item.TX_PLCA_CAVA,
            NM_MTRT: item.NM_MTRT,
            TX_TIPO_SRVC: item.TX_TIPO_SRVC,
            NM_FNTS_EPSA: item.NM_FNTS_EPSA,
            VL_SRVC: item.VL_SRVC.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            })
          };
        });
        this.maxPages = res.TOTAL_PAGES;
        this.isLoading = false;
      },
      error: (_error: any) => {
        this.isLoading = false;
      }
    });
  }

  exportarExcel() {
    const FK_EPSA = this.empresaId;
    const DT_INIC = this.form.controls['DT_INIC'].value;
    const DT_FIM = this.form.controls['DT_FIM'].value;
    if (!DT_FIM || !DT_INIC) {
      this.swalAlert.showAlert(
        'Atenção!',
        'Preencha os campos de data para exportar o relatório.',
        'warning'
      );
      return;
    }

    this.isLoading = true;
    this.reportService.GetOrdemServico(FK_EPSA, DT_INIC, DT_FIM).subscribe({
      next: (response: IGetReportBase64) => {
        this.appExportBase64.downloadExcel(
          response.ReportBase64,
          'lista-ordem-servico'
        );
        this.isLoading = false;
      },
      error: _error => {
        this.isLoading = false;
      }
    });
  }
}
